import { startTransition, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AUTH_MODE_PARAM,
  AUTH_PRODUCT_PARAM,
  AUTH_RETURN_TO_PARAM,
  AUTH_ROUTE_STATE_QUERY_PARAM_MAP,
  AUTH_SOURCE_PARAM,
  AUTH_VIEW_PARAM,
  SIGN_IN_PRODUCTS,
  SIGN_IN_VIEWS,
  SignInProduct,
  SignInView,
  TWO_FACTOR_MODES,
  TwoFactorMode,
} from 'common/constants/auth';

const LEGACY_AUTH_IDENTIFIER_PARAM = 'identifier';

export interface UpdateAuthRouteOptions {
  mode?: TwoFactorMode | null;
  product?: SignInProduct | null;
  replace?: boolean;
  returnTo?: string | null;
  source?: string | null;
  view?: SignInView | null;
}

export type UpdateAuthRouteState = (options: UpdateAuthRouteOptions) => void;

const useAuthRouteState = (defaultView: SignInView) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const routeState = useMemo(
    () => ({
      mode: TWO_FACTOR_MODES.includes(searchParams.get(AUTH_MODE_PARAM) as TwoFactorMode)
        ? (searchParams.get(AUTH_MODE_PARAM) as TwoFactorMode)
        : null,
      product: SIGN_IN_PRODUCTS.includes(searchParams.get(AUTH_PRODUCT_PARAM) as SignInProduct)
        ? (searchParams.get(AUTH_PRODUCT_PARAM) as SignInProduct)
        : null,
      returnTo: searchParams.get(AUTH_RETURN_TO_PARAM),
      source: searchParams.get(AUTH_SOURCE_PARAM),
      view: SIGN_IN_VIEWS.includes(searchParams.get(AUTH_VIEW_PARAM) as SignInView)
        ? (searchParams.get(AUTH_VIEW_PARAM) as SignInView)
        : defaultView,
    }),
    [defaultView, searchParams]
  );

  useEffect(() => {
    if (searchParams.get(AUTH_VIEW_PARAM) !== defaultView) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete(AUTH_VIEW_PARAM);

    if (nextSearchParams.toString() === searchParams.toString()) {
      return;
    }

    setSearchParams(nextSearchParams, { replace: true });
  }, [defaultView, searchParams, setSearchParams]);

  const updateRouteState = useCallback(
    ({ replace = true, ...updates }: UpdateAuthRouteOptions) => {
      // Auth flow state lives in query params; transition updates avoid blocking input-heavy steps.
      startTransition(() => {
        const nextSearchParams = new URLSearchParams(searchParams);

        // The sign-in identifier no longer belongs in auth URLs, so always strip any legacy value.
        nextSearchParams.delete(LEGACY_AUTH_IDENTIFIER_PARAM);

        Object.entries(updates).forEach(([key, value]) => {
          const queryKey = AUTH_ROUTE_STATE_QUERY_PARAM_MAP[key as keyof typeof AUTH_ROUTE_STATE_QUERY_PARAM_MAP];
          if (!queryKey) {
            return;
          }

          if (value === null || value === undefined || value === '') {
            nextSearchParams.delete(queryKey);
            return;
          }

          if (key === 'view' && value === defaultView) {
            nextSearchParams.delete(queryKey);
            return;
          }

          nextSearchParams.set(queryKey, value);
        });

        if (nextSearchParams.toString() === searchParams.toString()) {
          return;
        }

        setSearchParams(nextSearchParams, { replace });
      });
    },
    [defaultView, searchParams, setSearchParams]
  );

  return {
    ...routeState,
    updateRouteState,
  };
};

export default useAuthRouteState;
